import { useState } from "react";
import { useTranslation } from "react-i18next";
import { 
  GraduationCap, 
  Play,
  BookOpen,
  Rocket,
  Users,
  Target,
  TrendingUp,
  CheckCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function TutorialsPage() {
  const { t } = useTranslation();
  const [selectedTutorial, setSelectedTutorial] = useState(null);
  const [activeTab, setActiveTab] = useState("all");

  const tutorials = [
    {
      id: 1,
      titleKey: "tutorialsPage.tutorials.intro.title",
      descriptionKey: "tutorialsPage.tutorials.intro.description",
      durationKey: "tutorialsPage.tutorials.intro.duration",
      levelKey: "tutorialsPage.tutorials.intro.level",
      stepsKey: "tutorialsPage.tutorials.intro.steps",
      category: "basics"
    },
    {
      id: 2,
      titleKey: "tutorialsPage.tutorials.firstCampaign.title",
      descriptionKey: "tutorialsPage.tutorials.firstCampaign.description",
      durationKey: "tutorialsPage.tutorials.firstCampaign.duration",
      levelKey: "tutorialsPage.tutorials.firstCampaign.level",
      stepsKey: "tutorialsPage.tutorials.firstCampaign.steps",
      category: "basics"
    },
    {
      id: 3,
      titleKey: "tutorialsPage.tutorials.millionStrategy.title",
      descriptionKey: "tutorialsPage.tutorials.millionStrategy.description",
      durationKey: "tutorialsPage.tutorials.millionStrategy.duration",
      levelKey: "tutorialsPage.tutorials.millionStrategy.level",
      stepsKey: "tutorialsPage.tutorials.millionStrategy.steps",
      category: "million"
    },
    {
      id: 4,
      titleKey: "tutorialsPage.tutorials.abTesting.title",
      descriptionKey: "tutorialsPage.tutorials.abTesting.description",
      durationKey: "tutorialsPage.tutorials.abTesting.duration",
      levelKey: "tutorialsPage.tutorials.abTesting.level",
      stepsKey: "tutorialsPage.tutorials.abTesting.steps",
      category: "advanced"
    },
    {
      id: 5,
      titleKey: "tutorialsPage.tutorials.tracking.title",
      descriptionKey: "tutorialsPage.tutorials.tracking.description",
      durationKey: "tutorialsPage.tutorials.tracking.duration",
      levelKey: "tutorialsPage.tutorials.tracking.level",
      stepsKey: "tutorialsPage.tutorials.tracking.steps",
      category: "advanced"
    }
  ];

  const strategies = [
    {
      id: "telegram",
      titleKey: "tutorialsPage.strategies.telegram.title",
      stepsKey: "tutorialsPage.strategies.telegram.steps",
      tipKey: "tutorialsPage.strategies.telegram.tip",
      icon: Rocket,
      color: "text-blue-400"
    },
    {
      id: "multiChannel",
      titleKey: "tutorialsPage.strategies.multiChannel.title",
      stepsKey: "tutorialsPage.strategies.multiChannel.steps",
      tipKey: "tutorialsPage.strategies.multiChannel.tip",
      icon: Target,
      color: "text-green-400"
    },
    {
      id: "timeDistribution",
      titleKey: "tutorialsPage.strategies.timeDistribution.title",
      stepsKey: "tutorialsPage.strategies.timeDistribution.steps",
      tipKey: "tutorialsPage.strategies.timeDistribution.tip",
      icon: TrendingUp,
      color: "text-purple-400"
    }
  ];

  const filteredTutorials = activeTab === "all" 
    ? tutorials 
    : tutorials.filter(t => t.category === activeTab);

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="tutorials-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-black text-foreground">{t('tutorialsPage.title')}</h1>
        <p className="text-muted-foreground mt-1">{t('tutorialsPage.subtitle')}</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="all">{t('tutorialsPage.all')}</TabsTrigger>
          <TabsTrigger value="basics">{t('tutorialsPage.basics')}</TabsTrigger>
          <TabsTrigger value="million">{t('tutorialsPage.millionCampaigns')}</TabsTrigger>
          <TabsTrigger value="advanced">{t('tutorialsPage.advanced')}</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-6">
          {/* Tutorials Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredTutorials.map((tutorial) => {
              const steps = t(tutorial.stepsKey, { returnObjects: true }) || [];
              return (
                <Card 
                  key={tutorial.id} 
                  className="card-hover cursor-pointer"
                  onClick={() => setSelectedTutorial(tutorial)}
                  data-testid={`tutorial-${tutorial.id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-lg">{t(tutorial.titleKey)}</CardTitle>
                      <Badge variant="outline">
                        {t(tutorial.levelKey)}
                      </Badge>
                    </div>
                    <CardDescription>{t(tutorial.descriptionKey)}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Play className="w-4 h-4" />
                        {t(tutorial.durationKey)}
                      </span>
                      <span>{Array.isArray(steps) ? steps.length : 0} {t('tutorialsPage.steps')}</span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Selected Tutorial Detail */}
      {selectedTutorial && (
        <Card className="border-primary" data-testid="tutorial-detail">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-primary" />
                {t(selectedTutorial.titleKey)}
              </CardTitle>
              <Button variant="ghost" onClick={() => setSelectedTutorial(null)}>
                {t('tutorialsPage.close')}
              </Button>
            </div>
            <CardDescription>{t(selectedTutorial.descriptionKey)}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <h4 className="font-bold">{t('tutorialsPage.executionSteps')}</h4>
              <ol className="space-y-3">
                {(t(selectedTutorial.stepsKey, { returnObjects: true }) || []).map((step, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold">
                      {index + 1}
                    </span>
                    <span className="pt-1">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Million Strategy Section */}
      <Card className="border-yellow-500/50 bg-yellow-500/5" data-testid="million-strategy">
        <CardHeader>
          <CardTitle className="text-2xl">🚀 {t('tutorialsPage.millionStrategyTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {strategies.map((strategy) => {
            const Icon = strategy.icon;
            const steps = t(strategy.stepsKey, { returnObjects: true }) || [];
            return (
              <div key={strategy.id} className="p-4 rounded-lg bg-card border border-border">
                <h4 className={`font-bold text-lg mb-3 flex items-center gap-2 ${strategy.color}`}>
                  <Icon className="w-5 h-5" />
                  {t(strategy.titleKey)}
                </h4>
                <ol className="space-y-2 mb-4">
                  {(Array.isArray(steps) ? steps : []).map((step, stepIndex) => (
                    <li key={stepIndex} className="flex items-start gap-2 text-muted-foreground">
                      <CheckCircle className="w-4 h-4 text-green-400 mt-1 flex-shrink-0" />
                      <span>{step}</span>
                    </li>
                  ))}
                </ol>
                <div className="p-3 rounded bg-primary/10 text-sm">
                  💡 {t(strategy.tipKey)}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Quick Tips */}
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary" />
            {t('tutorialsPage.quickTipsForSuccess')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <h5 className="font-medium">✅ {t('tutorialsPage.doThis')}</h5>
              <ul className="text-sm text-muted-foreground space-y-1">
                {(t('tutorialsPage.doTips', { returnObjects: true }) || []).map((tip, index) => (
                  <li key={index}>• {tip}</li>
                ))}
              </ul>
            </div>
            <div className="space-y-2">
              <h5 className="font-medium">❌ {t('tutorialsPage.doNotDoThis')}</h5>
              <ul className="text-sm text-muted-foreground space-y-1">
                {(t('tutorialsPage.dontTips', { returnObjects: true }) || []).map((tip, index) => (
                  <li key={index}>• {tip}</li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
